import type { FormFieldDef } from '@los-workspace/workflow-runtime';

export type FieldCatalogItem = {
  code?: string;
  label?: string;
  dataType?: string;
  fieldType?: string;
  description?: string;
  validationRules?: unknown;
  validationConfig?: unknown;
  uiHints?: unknown;
  defaultValue?: unknown;
  optionsConfig?: unknown;
  [key: string]: unknown;
};

export type FieldCatalogDocument = {
  items?: FieldCatalogItem[];
};

/** Build a lookup map from pack fields catalog (`fields.json`). */
export function buildFieldCatalogMap(
  fields: unknown,
): Map<string, FieldCatalogItem> {
  const map = new Map<string, FieldCatalogItem>();
  const doc = fields as FieldCatalogDocument | null | undefined;
  for (const item of doc?.items ?? []) {
    const code = String(item?.code ?? '').trim();
    if (code) map.set(code, item);
  }
  return map;
}

/** Merge catalog metadata into an inline form field (catalog fills gaps only). */
export function enrichFieldFromCatalog(
  field: FormFieldDef,
  catalog: Map<string, FieldCatalogItem>,
): FormFieldDef {
  const entry = catalog.get(field.fieldCode);
  if (!entry) return field;

  return {
    ...field,
    label: field.label ?? entry.label,
    placeholder: field.placeholder ?? (entry['placeholder'] as string | undefined),
    dataType: field.dataType ?? entry.dataType,
    fieldType: field.fieldType ?? entry.fieldType,
    uiHints: {
      ...((entry.uiHints as Record<string, unknown>) ?? {}),
      ...((field['uiHints'] as Record<string, unknown>) ?? {}),
    },
    validationRules: coalesceValidationRules(field.validationRules, entry.validationRules),
    validationConfig: field['validationConfig'] ?? entry.validationConfig,
    validations: field['validations'] ?? entry['validations'],
    optionsConfig: field.optionsConfig ?? entry.optionsConfig,
    defaultValue: field.defaultValue ?? entry.defaultValue,
  };
}

function coalesceValidationRules(
  fieldRules: FormFieldDef['validationRules'],
  catalogRules: unknown,
): FormFieldDef['validationRules'] {
  if (fieldRules != null) return fieldRules;
  if (catalogRules == null) return undefined;
  if (Array.isArray(catalogRules)) return catalogRules;
  return [catalogRules];
}
